import { supabase } from "@/integrations/supabase/client";
import { getCurrentUserId } from "@/lib/authCache";

export type PopupTriggerType =
  | "everyone"
  | "community_all"
  | "entry_challenge_first_day"
  | "entry_challenge_last_day";

export type PopupCtaActionType = "internal_link" | "external_url";

/**
 * Server-side audience filter, applied in the `get_active_popups_for_user` RPC
 * (not client-side) so it reaches native clients without an app update.
 *   all            — no restriction
 *   paying         — caller has an active-access subscription_status
 *   min_period_end — caller's subscription_period_end >= audience_param (a proxy
 *                    for long-term / prepaid "tier" subs; param is an ISO cutoff)
 *   tier           — caller's subscription_tier matches audience_param, one of
 *                    'gold' | 'silver' | 'bronze', or 'none' for users with no
 *                    tier (subscription_tier IS NULL)
 *   prospect       — caller is a sy-subs funnel buyer (profiles.lifecycle_stage
 *                    = 'prospect') and NOT an active-access subscriber
 */
export type PopupAudienceKind = "all" | "paying" | "min_period_end" | "tier" | "prospect" | "list";

/** Valid audience_param values when audience_kind === 'tier'. */
export type PopupTierParam = "gold" | "silver" | "bronze" | "none";

export type PopupDisplayMode = "popup" | "banner";

/** Anchor for the prospect time-window filter (applied server-side in the RPC). */
export type PopupWindowAnchor = "since_start" | "before_cohort_end" | "after_cohort_end";

export interface PopupCampaign {
  id: string;
  title: string;
  is_active: boolean;
  priority: number;
  trigger_type: PopupTriggerType;
  // Optional here only because the generated Supabase types aren't regenerated yet;
  // the DB column is NOT NULL DEFAULT 'all'. The client never branches on these —
  // audience filtering happens server-side in the RPC — they exist for the admin form.
  audience_kind?: PopupAudienceKind;
  /** Interpreted per audience_kind. For min_period_end: an ISO timestamp cutoff. */
  audience_param?: string | null;
  /** 'popup' = modal (default), 'banner' = persistent strip. Server default 'popup'. */
  display_mode?: PopupDisplayMode;
  /**
   * Prospect time-window filter, applied server-side (get_active_*_for_user RPC).
   * Valid only when audience_kind === 'prospect' (a DB CHECK enforces this).
   * NULL anchor = any prospect. min/max are whole-day thresholds (floored), open when NULL.
   */
  prospect_window_anchor?: PopupWindowAnchor | null;
  prospect_window_min_days?: number | null;
  prospect_window_max_days?: number | null;
  starts_at: string | null;
  ends_at: string | null;
  heading: string | null;
  body_text: string | null;
  image_url: string | null;
  video_url: string | null;
  cta_label: string | null;
  cta_action_type: PopupCtaActionType | null;
  cta_value: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Active, in-window, not-yet-seen campaigns for the current user, priority-ordered. */
export async function fetchActivePopups(): Promise<PopupCampaign[]> {
  const { data, error } = await supabase.rpc("get_active_popups_for_user");
  if (error) throw error;
  return (data ?? []) as PopupCampaign[];
}

/** Active, in-window, audience-matched BANNER campaigns for the current user,
 * priority-ordered. Banners have no once-ever seen-ledger (client dismisses them). */
export async function fetchActiveBanners(): Promise<PopupCampaign[]> {
  // types.ts not regenerated yet for get_active_banners_for_user (added in Task 1's migration)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped until Lovable regenerates types.ts
  const { data, error } = await (supabase.rpc as any)("get_active_banners_for_user");
  if (error) throw error;
  return (data ?? []) as PopupCampaign[];
}

/**
 * True if the current user holds at least one enrollment in a community-type
 * challenge, regardless of which challenge is currently active. Backs the
 * `community_all` trigger (= ANY community enrollment, not "community active").
 *
 * Mirrors the verified pattern in userAccessService: `user_challenge_enrollments`
 * (FK `challenge_id` → `challenges`), embedded as `challenges!inner(challenge_type)`.
 * RLS already scopes selects to the caller's own rows; we keep the explicit
 * `user_id` filter for parity with the rest of the repo.
 */
export async function hasCommunityEnrollment(): Promise<boolean> {
  const userId = await getCurrentUserId();
  if (!userId) return false;
  const { data, error } = await supabase
    .from("user_challenge_enrollments")
    .select("challenge_id, challenges!inner(challenge_type)")
    .eq("user_id", userId)
    .eq("challenges.challenge_type", "community")
    .limit(1);
  if (error) throw error;
  return (data?.length ?? 0) > 0;
}

/** Records a once-ever "seen" row. user_id defaults to auth.uid() server-side. */
export async function markPopupSeen(campaignId: string): Promise<void> {
  const { error } = await supabase
    .from("popup_campaign_views")
    .upsert(
      { campaign_id: campaignId },
      { onConflict: "campaign_id,user_id", ignoreDuplicates: true },
    );
  if (error) throw error;
}

/** Flags the user's view row as having clicked the CTA. */
export async function markPopupCtaClicked(campaignId: string): Promise<void> {
  const { error } = await supabase
    .from("popup_campaign_views")
    .update({ cta_clicked: true })
    .eq("campaign_id", campaignId);
  if (error) throw error;
}
